import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import {
	Alert,
	Checkbox,
	Chip,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	MenuItem,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { memo } from "react";
import type { PersonAssignmentRetrieveType } from "../../api/assignments/person-assignment";

export interface FunnelPersonSummary {
	id: number;
	name: string;
	headline: string | null;
	grade: string;
	is_upvote: boolean;
}

export interface SharedPerson {
	id: number;
	name: string;
	headline: string | null;
}

// ---------------------------------------------------------------------------
// Single candidate + role picker — the card's variant of the person list
// ---------------------------------------------------------------------------

export const SinglePersonSection = ({
	person,
	roles,
	assignmentId,
	onAssignmentChange,
}: {
	person: SharedPerson;
	/** null while the person's boards load */
	roles: PersonAssignmentRetrieveType[] | null;
	assignmentId: number | null;
	onAssignmentChange: (id: number) => void;
}) => (
	<>
		<Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
			Role
		</Typography>
		{roles?.length === 0 ? (
			<Alert severity="info" data-component="no-role-alert">
				{person.name} is on no board yet. Add them to a board first, so the
				reviewer sees which role they are reviewed for.
			</Alert>
		) : (
			<TextField
				select
				data-component="review-role-select"
				size="small"
				fullWidth
				label="Review for role"
				disabled={roles === null}
				value={assignmentId ?? ""}
				onChange={(e) => onAssignmentChange(Number(e.target.value))}
			>
				{(roles ?? []).map((pa) => (
					<MenuItem key={pa.id} value={pa.assignment}>
						{pa.assignment_name}
					</MenuItem>
				))}
			</TextField>
		)}
		<Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
			Candidate
		</Typography>
		<ListItem data-component="shared-person" disableGutters>
			<ListItemText primary={person.name} secondary={person.headline} />
		</ListItem>
	</>
);

// ---------------------------------------------------------------------------
// Memoized person row — avoids re-rendering 400 rows on every keystroke
// ---------------------------------------------------------------------------

const PersonRow = memo(
	({
		person,
		checked,
		onToggle,
		onOpenPerson,
	}: {
		person: FunnelPersonSummary;
		checked: boolean;
		onToggle: (id: number) => void;
		onOpenPerson: (id: number) => void;
	}) => (
		<ListItem
			disablePadding
			secondaryAction={
				<IconButton
					data-component="open-person-button"
					size="small"
					onClick={(e) => {
						e.stopPropagation();
						onOpenPerson(person.id);
					}}
				>
					<OpenInNewIcon sx={{ fontSize: 16 }} />
				</IconButton>
			}
		>
			<ListItemButton
				onClick={() => onToggle(person.id)}
				data-component="person-list-item"
			>
				<Checkbox
					data-component="person-checkbox"
					checked={checked}
					size="small"
					sx={{ mr: 0.5 }}
				/>
				{person.is_upvote ? (
					<ThumbUpIcon
						data-component="icon-vote-up"
						fontSize="small"
						color="success"
						sx={{ mr: 1, flexShrink: 0 }}
					/>
				) : (
					<ThumbDownIcon
						data-component="icon-vote-down"
						fontSize="small"
						sx={{ mr: 1, flexShrink: 0, color: "text.disabled" }}
					/>
				)}
				<ListItemText
					primary={person.name}
					secondary={person.headline}
					slotProps={{ secondary: { noWrap: true } }}
				/>
			</ListItemButton>
		</ListItem>
	)
);

// ---------------------------------------------------------------------------
// Memoized person list — isolates selection state from text field state
// ---------------------------------------------------------------------------

export const PersonSelectionList = memo(
	({
		allPersons,
		selectedPersonIds,
		onToggle,
		onSelectAll,
		onClear,
		onOpenPerson,
	}: {
		allPersons: FunnelPersonSummary[];
		selectedPersonIds: Set<number>;
		onToggle: (id: number) => void;
		onSelectAll: (upvotesOnly: boolean) => void;
		onClear: () => void;
		onOpenPerson: (id: number) => void;
	}) => (
		<>
			<Typography
				variant="subtitle2"
				color="text.secondary"
				sx={{ mt: 2, mb: 1 }}
			>
				Candidates ({selectedPersonIds.size} selected)
			</Typography>
			<Stack direction="row" spacing={1} sx={{ mb: 1 }}>
				<Chip
					data-component="select-upvotes-chip"
					label="Select upvotes"
					size="small"
					onClick={() => onSelectAll(true)}
					variant="outlined"
				/>
				<Chip
					data-component="select-all-chip"
					label="Select all"
					size="small"
					onClick={() => onSelectAll(false)}
					variant="outlined"
				/>
				<Chip
					data-component="clear-selection-chip"
					label="Clear"
					size="small"
					onClick={onClear}
					variant="outlined"
				/>
			</Stack>
			<List
				data-component="person-selection-list"
				dense
				sx={{ maxHeight: 300, overflow: "auto" }}
			>
				{allPersons.map((person) => (
					<PersonRow
						key={person.id}
						person={person}
						checked={selectedPersonIds.has(person.id)}
						onToggle={onToggle}
						onOpenPerson={onOpenPerson}
					/>
				))}
			</List>
		</>
	)
);
